import { ETAPES, ETAPE_INFO, type Etape } from './etapes.js';
import { foyersPresents, taillePresente } from './foyers.js';
import { creerAleatoire, type Aleatoire, type Graine } from './prng.js';
import type { AffectationHote, Foyer, Groupe, OptionsRepartition, RepartitionInput } from './types.js';

export class ErreurRepartition extends Error {
  constructor(
    readonly code: 'etape_sans_hote' | 'hote_absent' | 'hote_multi_etapes',
    message: string,
    readonly etape?: Etape,
  ) {
    super(message);
    this.name = 'ErreurRepartition';
  }
}

const DEFAUTS: Required<OptionsRepartition> = {
  redemarrages: 60,
  iterations: 4000,
  temperatureInitiale: 2,
  refroidissement: 0.999,
};

/* ---------- représentation interne : indices entiers ---------- */

interface Unite {
  id: string;
  taille: number;
  personnes: number[]; // indices globaux des personnes présentes
  /** Capacité de table si l'unité reçoit (hôtes compris), Infinity sinon. */
  capacite: number;
}

/** Pour chaque étape : hotes[k] = index d'unité ; groupes[k] = indices d'unités (hôte inclus en tête). */
type Etat = Record<Etape, number[][]>;

interface Contexte {
  unites: Unite[];
  hotes: Record<Etape, number[]>;
  nbPersonnes: number;
  foyerDePersonne: number[];
}

/**
 * Moteur de répartition, porté de `prototype-algo-repartition.py` : unités = foyers,
 * hôtes fixés par étape, recuit simulé avec échanges de foyers de même taille
 * (plus déplacements simples tant que l'écart entre groupes reste ≤ 1).
 * Déterministe pour une graine donnée.
 */
export function repartir(input: RepartitionInput, graine: Graine, options: OptionsRepartition = {}): Groupe[] {
  const opts = { ...DEFAUTS, ...options };
  const ctx = construireContexte(input.foyers, input.hotes);
  const alea = creerAleatoire(graine);

  let meilleur: { cout: number; etat: Etat } | null = null;
  for (let r = 0; r < opts.redemarrages; r++) {
    const etat = etatInitial(ctx, alea);
    const compte = compterRencontres(ctx, etat);
    let cout = coutDepuis(compte);
    if (meilleur === null || cout < meilleur.cout) meilleur = { cout, etat: cloner(etat) };
    let T = opts.temperatureInitiale;
    for (let i = 0; i < opts.iterations && cout > 0; i++) {
      const mv = voisin(ctx, etat, alea);
      if (mv) {
        const delta = appliquer(ctx, etat, compte, mv);
        if (delta <= 0 || alea.suivant() < Math.exp(-delta / T)) {
          cout += delta;
          if (cout < meilleur.cout) meilleur = { cout, etat: cloner(etat) };
        } else {
          appliquer(ctx, etat, compte, inverse(mv));
        }
      }
      T *= opts.refroidissement;
    }
    if (meilleur.cout === 0) break;
  }
  return versGroupes(ctx, (meilleur as { etat: Etat }).etat);
}

function construireContexte(foyers: Foyer[], hotes: AffectationHote[]): Contexte {
  const presents = foyersPresents(foyers);
  const unites: Unite[] = [];
  const foyerDePersonne: number[] = [];
  let np = 0;
  for (const f of presents) {
    const personnes: number[] = [];
    for (const m of f.membres) {
      if (!m.participe) continue;
      personnes.push(np);
      foyerDePersonne.push(unites.length);
      np++;
    }
    unites.push({ id: f.id, taille: personnes.length, personnes, capacite: f.capaciteMax ?? Number.POSITIVE_INFINITY });
  }
  const index = new Map(unites.map((u, i) => [u.id, i]));
  const parEtape: Record<Etape, number[]> = { entree: [], plat: [], dessert: [] };
  const vus = new Set<string>();
  for (const h of hotes) {
    const i = index.get(h.foyerId);
    if (i === undefined) {
      const f = foyers.find((x) => x.id === h.foyerId);
      if (f && taillePresente(f) === 0) throw new ErreurRepartition('hote_absent', `${f.nom} ne participe plus et ne peut pas recevoir.`, h.etape);
      continue; // foyer inconnu : ignoré
    }
    // Une voiture peut recevoir à deux étapes (exception forcée, vérifiée par les règles), jamais deux fois à la même.
    const cle = `${h.foyerId}|${h.etape}`;
    if (vus.has(cle)) throw new ErreurRepartition('hote_multi_etapes', `${h.foyerId} est hôte deux fois ${ETAPE_INFO[h.etape].au}.`, h.etape);
    vus.add(cle);
    parEtape[h.etape].push(i);
  }
  for (const e of ETAPES) {
    if (parEtape[e].length === 0) throw new ErreurRepartition('etape_sans_hote', `Personne ne reçoit ${ETAPE_INFO[e].au}.`, e);
  }
  return { unites, hotes: parEtape, nbPersonnes: np, foyerDePersonne };
}

/* ---------- état initial : tirage aléatoire sous contrainte de taille (règle 6) ---------- */

function etatInitial(ctx: Contexte, alea: Aleatoire): Etat {
  const etat = {} as Etat;
  for (const e of ETAPES) etat[e] = etapeInitiale(ctx, e, alea);
  return etat;
}

/**
 * Tirage initial d'une étape : chaque unité rejoint une table qui a de la place (capacité de
 * l'hôte) en gardant les groupes aussi équilibrés que possible (règle 6). Si les capacités ne
 * suffisent pas, on dépasse le moins possible et verifierRepartition le signalera.
 */
function etapeInitiale(ctx: Contexte, etape: Etape, alea: Aleatoire): number[][] {
  const hotes = ctx.hotes[etape];
  const autres = ctx.unites.map((_, i) => i).filter((i) => !hotes.includes(i));
  const total = ctx.unites.reduce((s, u) => s + u.taille, 0);
  const base = Math.floor(total / hotes.length);
  const plafondEquilibre = base + (total % hotes.length === 0 ? 0 : 1);
  const capacites = hotes.map((h) => (ctx.unites[h] as Unite).capacite);
  let meilleur: { ecart: number; depassement: number; groupes: number[][] } | null = null;

  for (let essai = 0; essai < 500; essai++) {
    const groupes = hotes.map((h) => [h]);
    const tailles = hotes.map((h) => (ctx.unites[h] as Unite).taille);
    // les couples d'abord, puis les personnes seules, dans un ordre aléatoire
    const ordre = alea.melanger(autres).sort((a, b) => (ctx.unites[b] as Unite).taille - (ctx.unites[a] as Unite).taille);
    let ok = true;
    let depassement = 0;
    for (const u of ordre) {
      const t = (ctx.unites[u] as Unite).taille;
      // Priorité : équilibré ET dans la capacité ; sinon dans la capacité ; sinon le moins plein.
      let candidats = groupes.map((_, k) => k).filter((k) => (tailles[k] as number) + t <= Math.min(plafondEquilibre, capacites[k] as number));
      if (candidats.length === 0) {
        candidats = groupes.map((_, k) => k).filter((k) => (tailles[k] as number) + t <= (capacites[k] as number));
        if (candidats.length === 0) {
          const min = Math.min(...tailles);
          candidats = groupes.map((_, k) => k).filter((k) => tailles[k] === min);
          depassement += t;
          ok = false;
        }
      }
      const k = alea.choisir(candidats);
      (groupes[k] as number[]).push(u);
      tailles[k] = (tailles[k] as number) + t;
    }
    const ecart = Math.max(...tailles) - Math.min(...tailles);
    if (meilleur === null || depassement < meilleur.depassement || (depassement === meilleur.depassement && ecart < meilleur.ecart)) meilleur = { ecart, depassement, groupes };
    if (ok && ecart <= 1) return groupes;
  }
  return (meilleur as { groupes: number[][] }).groupes;
}

/* ---------- coût incrémental ---------- */

function cleP(ctx: Contexte, a: number, b: number): number {
  return a < b ? a * ctx.nbPersonnes + b : b * ctx.nbPersonnes + a;
}

function compterRencontres(ctx: Contexte, etat: Etat): Map<number, number> {
  const compte = new Map<number, number>();
  for (const e of ETAPES) {
    for (const g of etat[e]) {
      pourChaquePaire(ctx, g, (k) => compte.set(k, (compte.get(k) ?? 0) + 1));
    }
  }
  return compte;
}

function pourChaquePaire(ctx: Contexte, groupe: number[], fn: (k: number) => void): void {
  const ppl: number[] = [];
  for (const u of groupe) for (const p of (ctx.unites[u] as Unite).personnes) ppl.push(p);
  for (let i = 0; i < ppl.length; i++) {
    for (let j = i + 1; j < ppl.length; j++) {
      const a = ppl[i] as number;
      const b = ppl[j] as number;
      if (ctx.foyerDePersonne[a] === ctx.foyerDePersonne[b]) continue; // règle 3 : même foyer, jamais compté
      fn(cleP(ctx, a, b));
    }
  }
}

function coutDepuis(compte: Map<number, number>): number {
  let c = 0;
  for (const n of compte.values()) if (n > 1) c += n - 1;
  return c;
}

/* ---------- voisinage ---------- */

interface Mouvement {
  etape: Etape;
  a: number; // index de groupe
  b: number;
  deA: number[]; // unités de a qui partent vers b
  deB: number[];
}

function inverse(m: Mouvement): Mouvement {
  return { etape: m.etape, a: m.a, b: m.b, deA: m.deB, deB: m.deA };
}

function voisin(ctx: Contexte, etat: Etat, alea: Aleatoire): Mouvement | null {
  const etape = alea.choisir(ETAPES);
  const groupes = etat[etape];
  if (groupes.length < 2) return null;
  const [a, b] = alea.echantillon(groupes.map((_, k) => k), 2) as [number, number];
  const ga = (groupes[a] as number[]).slice(1); // sans l'hôte
  const gb = (groupes[b] as number[]).slice(1);
  const taille = (us: number[]): number => us.reduce((s, u) => s + (ctx.unites[u] as Unite).taille, 0);
  const tailles = groupes.map((g) => taille(g));
  const capacite = (k: number): number => (ctx.unites[(groupes[k] as number[])[0] as number] as Unite).capacite;
  const ecartActuel = Math.max(...tailles) - Math.min(...tailles);

  for (let essai = 0; essai < 50; essai++) {
    // Déplacement simple d'une unité : dans la capacité de la table d'arrivée, sans aggraver
    // l'écart entre groupes au-delà de 1 (ou de l'écart déjà imposé par les capacités).
    if (alea.suivant() < 0.3) {
      const [src, dst, gs] = alea.suivant() < 0.5 ? [a, b, ga] : [b, a, gb];
      if (gs.length === 0) continue;
      const u = alea.choisir(gs);
      const t = (ctx.unites[u] as Unite).taille;
      const apres = tailles.slice();
      apres[src] = (apres[src] as number) - t;
      apres[dst] = (apres[dst] as number) + t;
      if ((apres[dst] as number) <= capacite(dst) && Math.max(...apres) - Math.min(...apres) <= Math.max(1, ecartActuel)) {
        return src === a ? { etape, a, b, deA: [u], deB: [] } : { etape, a, b, deA: [], deB: [u] };
      }
      continue;
    }
    // Échange de 1 ou 2 unités de même taille totale (les tailles de tables ne bougent pas).
    if (ga.length === 0 || gb.length === 0) continue;
    const sa = alea.echantillon(ga, 1 + alea.entier(Math.min(2, ga.length)));
    const sb = alea.echantillon(gb, 1 + alea.entier(Math.min(2, gb.length)));
    if (taille(sa) === taille(sb)) return { etape, a, b, deA: sa, deB: sb };
  }
  return null;
}

/** Applique le mouvement, met à jour le compte des rencontres et renvoie la variation de coût. */
function appliquer(ctx: Contexte, etat: Etat, compte: Map<number, number>, m: Mouvement): number {
  const groupes = etat[m.etape];
  const ga = groupes[m.a] as number[];
  const gb = groupes[m.b] as number[];
  let delta = 0;
  const retirer = (k: number): void => {
    const n = compte.get(k) ?? 0;
    if (n > 1) delta -= 1;
    compte.set(k, n - 1);
  };
  const ajouter = (k: number): void => {
    const n = compte.get(k) ?? 0;
    if (n >= 1) delta += 1;
    compte.set(k, n + 1);
  };
  pourChaquePaire(ctx, ga, retirer);
  pourChaquePaire(ctx, gb, retirer);
  groupes[m.a] = ga.filter((u) => !m.deA.includes(u)).concat(m.deB);
  groupes[m.b] = gb.filter((u) => !m.deB.includes(u)).concat(m.deA);
  pourChaquePaire(ctx, groupes[m.a] as number[], ajouter);
  pourChaquePaire(ctx, groupes[m.b] as number[], ajouter);
  return delta;
}

function cloner(etat: Etat): Etat {
  const c = {} as Etat;
  for (const e of ETAPES) c[e] = etat[e].map((g) => g.slice());
  return c;
}

function versGroupes(ctx: Contexte, etat: Etat): Groupe[] {
  const out: Groupe[] = [];
  for (const e of ETAPES) {
    for (const g of etat[e]) {
      const ids = g.map((u) => (ctx.unites[u] as Unite).id);
      out.push({ etape: e, hoteFoyerId: ids[0] as string, foyerIds: ids });
    }
  }
  return out;
}

/* ---------- déplacement manuel (écran 5) ---------- */

/**
 * Déplace un foyer (bloc indivisible, règle 3) vers la table d'un autre hôte à la même étape.
 * Un hôte ne quitte jamais sa propre table. Renvoie une nouvelle liste de groupes.
 */
export function deplacerFoyer(groupes: Groupe[], etape: Etape, foyerId: string, versHoteFoyerId: string): Groupe[] {
  const source = groupes.find((g) => g.etape === etape && g.foyerIds.includes(foyerId));
  const cible = groupes.find((g) => g.etape === etape && g.hoteFoyerId === versHoteFoyerId);
  if (!source || !cible) throw new Error('Groupe source ou cible introuvable.');
  if (source.hoteFoyerId === foyerId) throw new Error("Un hôte ne peut pas quitter sa propre table.");
  if (source === cible) return groupes;
  return groupes.map((g) => {
    if (g === source) return { ...g, foyerIds: g.foyerIds.filter((id) => id !== foyerId) };
    if (g === cible) return { ...g, foyerIds: [...g.foyerIds, foyerId] };
    return g;
  });
}

/**
 * Échange deux foyers de tables différentes à la même étape (déplacement « en bloc » des deux côtés).
 */
export function echangerFoyers(groupes: Groupe[], etape: Etape, foyerA: string, foyerB: string): Groupe[] {
  const ga = groupes.find((g) => g.etape === etape && g.foyerIds.includes(foyerA));
  const gb = groupes.find((g) => g.etape === etape && g.foyerIds.includes(foyerB));
  if (!ga || !gb) throw new Error('Voiture introuvable à cette étape.');
  if (ga.hoteFoyerId === foyerA || gb.hoteFoyerId === foyerB) throw new Error("Un hôte ne peut pas quitter sa propre table.");
  if (ga === gb) return groupes;
  return groupes.map((g) => {
    if (g === ga) return { ...g, foyerIds: g.foyerIds.map((id) => (id === foyerA ? foyerB : id)) };
    if (g === gb) return { ...g, foyerIds: g.foyerIds.map((id) => (id === foyerB ? foyerA : id)) };
    return g;
  });
}
